import type { MessageAction, RequestAction } from "trystero";
import type { ControlReply } from "./online-room-types.js";
import { decodeBoundedRoomPacket, encodeRoomPacket } from "./room-packet.js";
import type { OnlineRoomAuthority } from "./room-protocol.js";
import type { RoomChat, RoomHello } from "./room-types.js";
import type { TrysteroRoomTransport } from "./trystero-room.js";

export class OnlineRoomChat {
  readonly #broadcast: MessageAction<string>;
  readonly #request: RequestAction<string, string>;
  #sequence = 0;

  constructor(
    transport: TrysteroRoomTransport,
    private readonly hello: RoomHello,
    private readonly authority: () => OnlineRoomAuthority | undefined,
    private readonly hostPeer: () => string | undefined,
    private readonly messages: () => readonly RoomChat[],
    private readonly update: (messages: readonly RoomChat[]) => void,
  ) {
    this.#broadcast = transport.action("chat");
    this.#request = transport.requestAction("chat-request");
    this.bind();
  }

  async send(text: string, selfPeerId: string): Promise<void> {
    const id = `${this.hello.sessionId}:${++this.#sequence}`;
    const authority = this.authority();
    if (authority) {
      const message = authority.chat(selfPeerId, id, text);
      this.append(message);
      await this.#broadcast.send(encodeRoomPacket(message));
      return;
    }
    const host = this.hostPeer();
    if (!host) throw new TypeError("Room host is not connected");
    const raw = await this.#request.request(encodeRoomPacket({ id, text }), {
      target: host,
      timeoutMs: 5_000,
    });
    const reply = decodeBoundedRoomPacket<ControlReply>(raw);
    if (!reply.ok) throw new TypeError(reply.reason ?? "Chat rejected");
  }

  private bind(): void {
    this.#broadcast.onMessage = (raw, context) => {
      if (context.peerId === this.hostPeer()) this.append(decodeBoundedRoomPacket<RoomChat>(raw));
    };
    this.#request.onRequest = async (raw, context) => {
      const authority = this.authority();
      if (!authority) throw new TypeError("Only the room host accepts chat");
      try {
        const request = decodeBoundedRoomPacket<{ id: string; text: string }>(raw);
        const message = authority.chat(context.peerId, request.id, request.text);
        this.append(message);
        await this.#broadcast.send(encodeRoomPacket(message));
        return encodeRoomPacket({ ok: true } satisfies ControlReply);
      } catch (error) {
        return encodeRoomPacket({ ok: false, reason: errorMessage(error) } satisfies ControlReply);
      }
    };
  }

  private append(message: RoomChat): void {
    const byId = new Map(this.messages().map((entry) => [entry.id, entry]));
    byId.set(message.id, message);
    this.update([...byId.values()].slice(-100));
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Online room failed";
}
